import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchFormSession, startFormSession, submitFormSession, getFormSessionErrorMessage } from '../utils/formSessionApi';
import { portalHrefForEntity, sessionShowsCompleted, splitIdFromCompletion } from '../utils/formMagicLinkRouting';
import { wizardSpecForFormType } from '../utils/formTypeRegistry';
import './FormMagicLinkLanding.css';

const EMPTY_PREFILL = Object.freeze({});
const EMPTY_WIZARD_STEPS = Object.freeze([]);

function sessionPrefillWithFeatures(session) {
  const prefill = { ...(session?.prefill ?? EMPTY_PREFILL) };
  const features = session?.enrichment_metadata
    ?.enrichment_sources?.rentcast?.metadata_only?.property_features;
  if (features && !prefill.property_features) {
    prefill.property_features = features;
  }
  return prefill;
}

function goToPortal(session, result = {}) {
  const entityId = result.entity_id
    || result.entityId
    || result.listingId
    || result.contractId
    || result.buyerAgreementId
    || session?.created_entity_id;
  const entityType = result.entity_type || result.entityType || session?.created_entity_type;
  window.location.href = portalHrefForEntity({
    entityId,
    entityType,
    formType: session?.form_type,
    splitAttachmentId: splitIdFromCompletion(session, result),
  });
}

const FormMagicLinkLanding = () => {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [skipError, setSkipError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchFormSession(token);
        if (data.status !== 'submitted' && data.status !== 'submitting') {
          try {
            await startFormSession(token);
          } catch {
            // Non-fatal: GET already validated the session; submit still works.
          }
        }
        if (!cancelled) setSession(data);
      } catch (err) {
        if (!cancelled) {
          setError(getFormSessionErrorMessage(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (!session || session.status !== 'submitting') return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const data = await fetchFormSession(token);
        if (!cancelled) setSession(data);
      } catch {
        // Keep the in-progress card until submit finishes or the user retries.
      }
    }, 1500);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [session, token]);

  useEffect(() => {
    if (!session?.can_skip_wizard) return undefined;
    if (session.status === 'submitted' || session.status === 'submitting') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const result = await submitFormSession(token, session.prefill || {});
        if (!cancelled) {
          goToPortal(session, result);
        }
      } catch (err) {
        if (!cancelled) {
          setSkipError(getFormSessionErrorMessage(err));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [session, token]);

  if (loading) {
    return (
      <div className="form-magic-page">
        <div className="form-magic-card">Loading your form…</div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="form-magic-page">
        <div className="form-magic-card form-magic-error">
          <h2>Link unavailable</h2>
          <p>{error || 'This link is invalid or has expired.'}</p>
          <p>Email <a href="mailto:tc@keyflo.ai">tc@keyflo.ai</a> to request a new form.</p>
        </div>
      </div>
    );
  }

  if (session.status === 'submitting' || (session.can_skip_wizard && !skipError && session.status !== 'submitted')) {
    return (
      <div className="form-magic-page">
        <div className="form-magic-card">Creating your form…</div>
      </div>
    );
  }

  const entityId = session.created_entity_id;
  const alreadyDone = sessionShowsCompleted(session);
  const prefill = sessionPrefillWithFeatures(session);
  const commonProps = {
    mode: 'page',
    isOpen: true,
    initialValues: prefill,
    wizardSteps: session.wizard_steps ?? EMPTY_WIZARD_STEPS,
    optionalWizardFields: session.optional_wizard_fields || [],
    stillNeededLabels: session.still_needed_labels || {},
    allowEarlySubmitAfter: session.allow_early_submit_after || '',
    formSessionToken: token,
    userEmail: session.user_email,
    initialError: skipError,
    onClose: (result) => goToPortal(session, {
      ...result,
      entity_id: result?.entity_id || result?.entityId || result?.listingId
        || result?.contractId || result?.buyerAgreementId || entityId,
      entity_type: result?.entity_type || result?.entityType || session?.created_entity_type,
    }),
  };

  const spec = wizardSpecForFormType(session.form_type, {
    createsEntity: session.creates_entity,
  });
  if (!spec) {
    return (
      <div className="form-magic-page">
        <div className="form-magic-card form-magic-error">
          <p>Unsupported form type.</p>
        </div>
      </div>
    );
  }

  const Wizard = spec.Wizard;
  return (
    <Wizard
      {...spec.buildLandingProps({
        commonProps,
        alreadyDone,
        prefill,
        entityId,
      })}
    />
  );
};

export default FormMagicLinkLanding;
